import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useContext } from 'react';
import { LanguageContext } from '../../contexts/LanguageContext';
import Forbidden from '../../pages/Forbidden';
import { UserRole } from '../../types';

// V17.516 (Phase 0, brief 2026-10-25-003 step 8): every refusal below renders
// <Forbidden> IN PLACE — the URL stays, the missing code(s) show, no redirect.
// Before, each branch redirected to the user's default page (RefuseTo), which
// users read as "I clicked X and landed on Y with no message" (R8 / S7).
// Layout's no-workspace safety net is separate and unchanged.

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;  // DEPRECATED: Use requirePermission="users.edit" instead
  requireApprover?: boolean;  // DEPRECATED: Use requirePermission="customers.approve" instead
  requiredRoles?: UserRole[];  // DEPRECATED: V5.0 - Use requirePermission instead
  requirePermission?: string;  // V7.0 - Permission-based access control (PREFERRED)
  requireAnyPermission?: string[];  // V7.0 - Requires ANY of the listed permissions
  requireAllPermissions?: string[];  // V7.0 - Requires ALL of the listed permissions
  requireRoleAssignment?: string;  // V17.128 - Require a role assignment by name (multi-role; checks assignment set, not active role)
  // V17.262: OR semantics. `requirePermission` and `requireRoleAssignment` are
  // independent early-return checks, so setting both ANDs them. The team board
  // needs either-or: officers arrive by role assignment, executives by
  // permission. Additive — existing routes are untouched.
  requireAnyOf?: { permissions?: string[]; roleAssignments?: string[] };
  excludeScouts?: boolean;  // V17.28.1 - Block Commercial Scouts from accessing sales rep pages
}

/**
 * V17.520 (Phase 0c, brief 2026-10-26-001, F26): the wait while the login is checked at start.
 * Lao by default (the shared English `Loading` stays as it is for other pages).
 */
export function BootLoading() {
  const language = useContext(LanguageContext)?.language ?? 'lo';
  return (
    <div className="flex items-center justify-center py-12" lang={language}>
      <div className="spinner"></div>
      <span className="ml-3 text-gray-600 dark:text-gray-300">{language === 'en' ? 'Loading...' : 'ກຳລັງໂຫຼດ...'}</span>
    </div>
  );
}

export default function ProtectedRoute({
  children,
  requireAdmin = false,
  requireApprover = false,
  requiredRoles,
  requirePermission,
  requireAnyPermission,
  requireAllPermissions,
  requireRoleAssignment,
  requireAnyOf,
  excludeScouts = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isAdmin, isApprover, user, hasRoleAssignment } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  if (isLoading) {
    return <BootLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // V17.28.1 - Exclude Commercial Scouts from accessing sales rep pages
  // Scouts are identified by having commercial.leads.create permission but NOT being a Commercial Sales Rep
  // V17.63: Team Leaders (commercial.leads.assign) are NOT scouts - they need pipeline/dashboard access
  if (excludeScouts) {
    const isTeamLeader = hasPermission('commercial.leads.assign');
    const isScout = hasPermission('commercial.leads.create') && user?.role !== 'sales_rep_commercial' && !isTeamLeader;
    if (isScout) {
      return <Forbidden roleBased />;
    }
  }

  // V17.262: OR gate. Additive check — does not replace requirePermission /
  // requireRoleAssignment below, which continue to AND when both are set.
  if (requireAnyOf) {
    const byPermission = (requireAnyOf.permissions ?? []).some((p) => hasPermission(p));
    const byRole = (requireAnyOf.roleAssignments ?? []).some((r) => hasRoleAssignment(r));
    if (!byPermission && !byRole) {
      return <Forbidden anyOf={requireAnyOf.permissions ?? []} roleBased={(requireAnyOf.roleAssignments ?? []).length > 0} />;
    }
  }

  // V7.0 - Permission-based access control (HIGHEST PRIORITY - PREFERRED METHOD)
  // Check single permission requirement
  if (requirePermission && !hasPermission(requirePermission)) {
    return <Forbidden required={[requirePermission]} />;
  }

  // V7.0 - Check "any permission" requirement (OR logic)
  if (requireAnyPermission && requireAnyPermission.length > 0) {
    if (!hasAnyPermission(requireAnyPermission)) {
      return <Forbidden anyOf={requireAnyPermission} />;
    }
  }

  // V7.0 - Check "all permissions" requirement (AND logic)
  if (requireAllPermissions && requireAllPermissions.length > 0) {
    if (!hasAllPermissions(requireAllPermissions)) {
      return <Forbidden required={requireAllPermissions.filter((p) => !hasPermission(p))} />;
    }
  }

  // V17.128 - Role-assignment gate (multi-role support).
  // Checks the user's full UserRoleAssignment set, not the currently-active role.
  // Use this for workspaces where presence on the user's role-assignment list grants
  // access (e.g., AR Officer - Residential can reach /ar/residential/officer even if
  // currently active under a different role). Sibling to backend `user_has_role_assignment`
  // helper (PR #1025).
  if (requireRoleAssignment && !hasRoleAssignment(requireRoleAssignment)) {
    return <Forbidden roleBased />;
  }

  // DEPRECATED: V5.0 - Granular role-based access control (use requirePermission instead)
  if (requiredRoles && requiredRoles.length > 0) {
    const userRole = user?.role;
    if (!userRole || !requiredRoles.includes(userRole)) {
      return <Forbidden roleBased />;
    }
  }

  // DEPRECATED: Legacy admin check — isAdmin() is hasPermission('users.edit') since V7.0
  if (requireAdmin && !isAdmin()) {
    return <Forbidden required={['users.edit']} />;
  }

  // DEPRECATED: Legacy approver check — isApprover() is hasPermission('customers.approve') since V7.0
  if (requireApprover && !isApprover()) {
    return <Forbidden required={['customers.approve']} />;
  }

  return <>{children}</>;
}
